import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { useEffect, useTransition } from "react";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import type { ScheduledPostsQuery } from "./__generated__/ScheduledPostsQuery.graphql";
import type { postsAgendados_posts$key } from "./__generated__/postsAgendados_posts.graphql";
import type { postsAgendadosQuery } from "./__generated__/postsAgendadosQuery.graphql";
import { Heading } from "@/components/heading";
import { PostPreview } from "@/ui/post-preview";
import { Spinner } from "@/components/ui/spinner";

export const Route = createFileRoute("/_visibility/_topnav/posts-agendados")({
	component: RouteComponent,
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "__root__" });
	const [isPending, startTransition] = useTransition();
	const [ref, entry] = useIntersectionObserver({
		threshold: 0,
		root: null,
		rootMargin: "0px",
	});

	const data = useLazyLoadQuery<postsAgendadosQuery>(
		graphql`
	    query postsAgendadosQuery($club: String!) {
				club(slug: $club) {
					id
					...postsAgendados_posts @arguments(club: $club)
				}
			}
	`,
		{
			club: club.slug,
		},
	);

	const {
		data: posts,
		hasNext,
		loadNext,
		isLoadingNext,
	} = usePaginationFragment<ScheduledPostsQuery, postsAgendados_posts$key>(
		graphql`
	  fragment postsAgendados_posts on Club
		@argumentDefinitions(
			club: { type: "String!" }
			first: { type: "Int" }
			cursor: { type: "String" }
		)
		@refetchable(queryName: "ScheduledPostsQuery")
		{
			scheduledContent(first: $first, after: $cursor)
			@connection(key: "posts_scheduledContent")
			{
			  edges {
			    node {
						... on ContentInterface {
						  id
				      ...postPreviewFragment @arguments(club: $club)
						}

						... on CourseComment {
							id
						}

						__typename
			    }
			  }
			  pageInfo {
			    hasNextPage
					totalCount
			  }
			}
		}
	`,
		data.club,
	);

	useEffect(() => {
		if (entry?.isIntersecting && hasNext && !isLoadingNext) {
			startTransition(() => {
				loadNext(10);
			});
		}
	}, [entry?.isIntersecting, hasNext, isLoadingNext, loadNext]);

	return (
		<div className="w-full flex items-start justify-center p-6">
			<div className="w-full max-w-[600px] flex flex-col gap-6">
				<Heading>
					Posts Agendados ({posts.scheduledContent.pageInfo.totalCount})
				</Heading>

				{posts.scheduledContent.edges
					?.filter(Boolean)
					.map(({ node }) =>
						node.__typename === "Post" ? (
							<PostPreview key={node.id} post={node} />
						) : null,
					)}

				<div ref={ref} className="size-1" />

				{isPending && <Spinner />}
			</div>
		</div>
	);
}
